import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, buttonClasses, EmptyState, ErrorState, Field, Icon, Select, Skeleton } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { useConnectorEndpoints, type ConnectorEndpoint } from "./use-connector-endpoints";

const C = "engage.workflows.connector.endpoint";

interface EndpointFieldProps {
  value: string;
  disabled: boolean;
  onChange: (endpointId: string) => void;
}

function AuthNote({ endpoint }: { endpoint: ConnectorEndpoint }) {
  const { t } = useTranslation();
  return (
    <p className="mt-2 flex items-start gap-2 text-xs text-fg-muted" data-testid="connector-auth-note">
      <Icon name={endpoint.auth ? "lock" : "info"} className="mt-0.5 size-3.5 shrink-0" />
      {endpoint.auth ? t(`${C}.authSet`, { header: endpoint.auth.header, last4: endpoint.auth.last4 }) : t(`${C}.authNone`)}
    </p>
  );
}

/** The endpoint a Data connector request calls: only endpoints set to "Only from workflows". */
export function ConnectorEndpointField({ value, disabled, onChange }: EndpointFieldProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const state = useConnectorEndpoints();
  const webhooks = consoleRoutes.settings(summary.slug, "webhooks");
  if (state.loading) {
    return (
      <div role="status" aria-label={t(`${C}.loading`)} className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (state.failed) return <ErrorState headingLevel={3} title={t(`${C}.loadFailedTitle`)} description={t(`${C}.loadFailedBody`)} retryLabel={t(`${C}.retry`)} onRetry={state.retry} />;
  if (!state.readable) return <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" role="note">{t(`${C}.noAccess`)}</p>;
  if (state.endpoints.length === 0 && !value) {
    return (
      <EmptyState
        icon="webhook"
        headingLevel={3}
        title={t(`${C}.emptyTitle`)}
        description={t(`${C}.emptyBody`)}
        action={<AppLink className={buttonClasses({ variant: "secondary", size: "sm" })} href={webhooks}>{t(`${C}.add`)}</AppLink>}
      />
    );
  }
  const chosen = state.endpoints.find((endpoint) => endpoint.id === value);
  const unavailable = value !== "" && !chosen?.enabled;
  return (
    <div>
      <Field label={t(`${C}.label`)} hint={t(`${C}.hint`)} error={unavailable ? t(`${C}.unavailable`) : undefined}>
        <Select value={value} disabled={disabled} onChange={(event) => { onChange(event.target.value); }}>
          <option value="">{t(`${C}.choose`)}</option>
          {value !== "" && chosen === undefined ? <option value={value}>{t(`${C}.unavailableOption`)}</option> : null}
          {state.endpoints.map((endpoint) => (
            <option key={endpoint.id} value={endpoint.id} disabled={!endpoint.enabled && endpoint.id !== value}>
              {endpoint.enabled ? endpoint.name : t(`${C}.disabledOption`, { name: endpoint.name })}
            </option>
          ))}
        </Select>
      </Field>
      {chosen ? <AuthNote endpoint={chosen} /> : null}
      <p className="mt-2 text-xs">
        <AppLink className="font-semibold text-primary-ink underline" href={webhooks}>{t(`${C}.openWebhooks`)}</AppLink>
      </p>
    </div>
  );
}
